import { useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { banUser, getPlayerFlags, listFlags, markPlayerFlagsReviewed, rescanFlags, type FlagSource, type PlayerFlagSummary } from "@/lib/api";
import { formatTimestamp } from "@/lib/utils";
import { FullScanPanel } from "@/components/FullScanPanel";
import { createSubmitLock } from "@/components/player-actions/action-model";
import { FlagRulesPanel } from "@/components/FlagRulesPanel";

import { FlagDetails, parseReasons, SOURCE_LABEL } from "@/components/FlagDetails";

const PAGE_SIZE = 50;
function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

export function FlagsRoute() {
  const [params, setParams] = useSearchParams();
  const [openPlayer, setOpenPlayer] = useState<string | null>(null);
  const [banNotice, setBanNotice] = useState<{ message: string; error: boolean } | null>(null);
  const qc = useQueryClient();
  const quickBanLock = useRef(createSubmitLock());
  const quickBan = useMutation({
    mutationFn: async ({ playerId, generation, reason }: { playerId: string; generation: number; reason: string }) => {
      await banUser(playerId, reason);
      try {
        await markPlayerFlagsReviewed(playerId, generation);
        return { message: `${playerId} permanently banned and marked reviewed.`, error: false };
      } catch (error) {
        return { message: `${playerId} was banned, but marking reviewed failed: ${errorMessage(error)}. Check the latest findings and use Mark reviewed.`, error: true };
      }
    },
    onSuccess: (notice) => {
      setBanNotice(notice);
      void qc.invalidateQueries({ queryKey: ["flags"] });
      void qc.invalidateQueries({ queryKey: ["flag-player"] });
      void qc.invalidateQueries({ queryKey: ["player-bans"] });
    },
    onError: (error) => setBanNotice({ message: `Ban failed: ${errorMessage(error)}`, error: true }),
    onSettled: () => quickBanLock.current.end(),
  });
  const rescan = useMutation({
    mutationFn: ({ playerId, generation }: { playerId: string; generation: number }) => rescanFlags(playerId, generation),
    onSuccess: (result, { playerId }) => {
      const fallback = result.fallbackSources.length
        ? ` No current snapshot for ${result.fallbackSources.map(source => SOURCE_LABEL[source as FlagSource]).join(", ")}; rechecked retained payloads.` : "";
      setBanNotice({ message: `${playerId}: rescan complete, ${result.details.length} finding${result.details.length === 1 ? "" : "s"} remain.${fallback}${result.skipped ? ` ${result.skipped} finding(s) kept because their payload is unavailable.` : ""}`,
        error: result.skipped > 0 });
    },
    onError: (error) => setBanNotice({ message: `Rescan failed: ${errorMessage(error)}. Refresh the findings before retrying.`, error: true }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["flags"] });
      void qc.invalidateQueries({ queryKey: ["flag-player"] });
    },
  });
  const source = params.get("source") ?? "";
  const reviewed = params.get("reviewed") === "true";
  const rulesTab = params.get("tab") === "rules";
  const includeBanned = params.get("includeBanned") === "true";
  const includeWhitelisted = params.get("includeWhitelisted") === "true";
  const rawOffset = Number(params.get("offset") ?? 0);
  const offset = Number.isSafeInteger(rawOffset) && rawOffset > 0 ? rawOffset : 0;
  const query = useQuery({
    queryKey: ["flags", source, reviewed, includeBanned, includeWhitelisted, offset],
    queryFn: () => listFlags({
      source: (source || undefined) as FlagSource | undefined,
      reviewed,
      includeBanned, includeWhitelisted, limit: PAGE_SIZE, offset,
    }),
    enabled: !rulesTab,
  });

  function setParam(key: string, value: string | undefined) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    next.delete("offset");
    setOpenPlayer(null);
    setParams(next, { replace: true });
  }

  function setOffset(nextOffset: number) {
    const next = new URLSearchParams(params);
    if (nextOffset <= 0) next.delete("offset"); else next.set("offset", String(nextOffset));
    setOpenPlayer(null);
    setParams(next, { replace: true });
  }

  const total = query.data?.total ?? 0;
  const rows = query.data?.rows ?? [];
  function switchTab(tab: 'unreviewed' | 'reviewed' | 'rules') {
    const next = new URLSearchParams(params);
    next.delete('offset'); next.delete('tab'); next.delete('reviewed');
    if (tab === 'reviewed') next.set('reviewed', 'true');
    if (tab === 'rules') next.set('tab', 'rules');
    setOpenPlayer(null); setParams(next, { replace: true });
  }

  return <AppShell title="Flags" subtitle={rulesTab ? "Flagging conditions and editable limits" : `${total} ${reviewed ? "reviewed" : "unreviewed"} player${total === 1 ? "" : "s"} — hard violations are automatically banned`}>
    <div className="space-y-4 max-w-6xl">
      <nav aria-label="Flags sections" className="flex flex-wrap gap-2 border-b border-border pb-3">
        <Button variant={!rulesTab && !reviewed ? 'default' : 'secondary'} aria-pressed={!rulesTab && !reviewed} onClick={() => switchTab('unreviewed')}>Unreviewed</Button>
        <Button variant={!rulesTab && reviewed ? 'default' : 'secondary'} aria-pressed={!rulesTab && reviewed} onClick={() => switchTab('reviewed')}>Reviewed</Button>
        <Button variant={rulesTab ? 'default' : 'secondary'} aria-pressed={rulesTab} onClick={() => switchTab('rules')}>Flag rules</Button>
      </nav>
      {rulesTab ? <FlagRulesPanel /> : <>
      <FullScanPanel />
      <p className="text-xs text-muted-foreground">
        One queue entry per player. Identical source and reason combinations increase their count; the newest five distinct findings keep details.
        Older details are discarded while the player’s total and first/last dates remain.
        Unreviewed flags hold players off the leaderboard. Mark reviewed only when it is appropriate to allow
        a fresh validated submission; reviewing does not restore a held score or remove a ban.
        Whitelisted developers remain exempt from anti-cheat holds, but not duplicate-purchase holds or bans.
        Rescan refreshes findings using the latest uploaded data and current checks; it cannot read unsynced offline progress.
      </p>
      <Alert><AlertDescription>
        History mismatches need investigation; they are not proof of cheating. Removed (R) is the client’s recorded removal total.
        Available (A) is its starting amount plus recorded additions, not a server-verified balance.
        Migration, inventory bookkeeping, or save recovery can affect these counters. Repeated warnings can describe the same unresolved mismatch.
      </AlertDescription></Alert>
      <div className="flex flex-wrap items-center gap-2">
        <select aria-label="Source" value={source} onChange={(e) => setParam("source", e.target.value)} className="h-9 rounded-md border border-input bg-background px-3 text-sm">
          <option value="">All sources</option>
          <option value="save_check">Save check</option>
          <option value="player_report">Player report</option>
          <option value="iap_duplicate">Duplicate purchase</option>
          <option value="leaderboard_submit">Leaderboard submission</option>
          <option value="full_scan">Full scan</option>
        </select>
        <label className="flex items-center gap-2 px-2 text-sm cursor-pointer">
          <input type="checkbox" checked={includeBanned} onChange={(e) => setParam("includeBanned", e.target.checked ? "true" : undefined)} className="accent-primary" />
          Show banned players
        </label>
        <label className="flex items-center gap-2 px-2 text-sm cursor-pointer">
          <input type="checkbox" checked={includeWhitelisted} onChange={(e) => setParam("includeWhitelisted", e.target.checked ? "true" : undefined)} className="accent-primary" />
          Show dev accounts
        </label>
        {(source || includeBanned || includeWhitelisted) && <Button variant="ghost" size="sm" onClick={() => { setOpenPlayer(null); setParams(reviewed ? { reviewed: 'true' } : {}, { replace: true }); }}>Clear filters</Button>}
      </div>
      {source && <p className="text-xs text-muted-foreground">Source filter matches retained findings for each player.</p>}
      {banNotice && <Alert variant={banNotice.error ? "destructive" : "default"}><AlertDescription>{banNotice.message}</AlertDescription></Alert>}
      {query.error && <Alert variant="destructive"><AlertDescription>{errorMessage(query.error)}</AlertDescription></Alert>}
      <div className="rounded-md border border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs text-muted-foreground"><tr>
            <th className="px-3 py-2 text-left">Player</th>
            <th className="px-3 py-2 text-left whitespace-nowrap">First flagged</th>
            <th className="px-3 py-2 text-left whitespace-nowrap">Last flagged</th>
            <th className="px-3 py-2 text-left" title="Flag submissions, including repeats; not independent incidents">Submissions</th>
            <th className="px-3 py-2 text-left">Latest finding</th>
            <th className="px-3 py-2 text-left">Status</th>
            <th className="px-3 py-2"><span className="sr-only">Actions</span></th>
          </tr></thead>
          <tbody>
            {query.isPending ? Array.from({ length: 5 }, (_, i) => <tr key={i} className="border-t border-border/50">
              {Array.from({ length: 7 }, (_, j) => <td key={j} className="px-3 py-3"><Skeleton className="h-4 w-20" /></td>)}
            </tr>) : rows.length === 0 ? <tr><td colSpan={7} className="px-3 py-8 text-center text-xs text-muted-foreground">
              {query.error ? "Could not load flags." : offset > 0 ? "No players on this page. Go to the previous page." : source || includeBanned || includeWhitelisted ? "No players match these filters." : reviewed ? "No reviewed players." : "No flags to review."}
            </td></tr> : rows.map((row) => <FlagRow key={row.player_id} row={row} expanded={openPlayer === row.player_id} onToggle={() => setOpenPlayer(openPlayer === row.player_id ? null : row.player_id)} onReviewed={() => void qc.invalidateQueries({ queryKey: ["flags"] })} banPending={quickBan.isPending || rescan.isPending} banning={quickBan.isPending && quickBan.variables?.playerId === row.player_id} rescanning={rescan.isPending && rescan.variables?.playerId === row.player_id} onRescan={(generation) => {
              setBanNotice(null);
              rescan.mutate({ playerId: row.player_id, generation });
            }} onQuickBan={(generation) => {
              if (!quickBanLock.current.begin()) return;
              setBanNotice(null);
              quickBan.mutate({ playerId: row.player_id, generation, reason: parseReasons(row.latest_reasons).join("; ") || "Flagged for moderation" });
            }} />)}
          </tbody>
        </table>
      </div>
      {(total > PAGE_SIZE || offset > 0) && <div className="flex items-center justify-between gap-4">
        <span className="text-xs text-muted-foreground">{rows.length ? `Showing ${offset + 1}–${offset + rows.length} of ${total} players` : `${total} players`}</span>
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))} disabled={offset === 0}>← Prev</Button>
          <Button variant="secondary" size="sm" onClick={() => setOffset(offset + PAGE_SIZE)} disabled={offset + PAGE_SIZE >= total}>Next →</Button>
        </div>
      </div>}
      </>}
    </div>
  </AppShell>;
}

function FlagRow({ row, expanded, onToggle, onReviewed, onQuickBan, banPending, banning, onRescan, rescanning }: {
  row: PlayerFlagSummary; expanded: boolean; onToggle: () => void; onReviewed: () => void;
  onQuickBan: (generation: number) => void; banPending: boolean; banning: boolean;
  onRescan: (generation: number) => void; rescanning: boolean;
}) {
  const qc = useQueryClient();
  const detail = useQuery({ queryKey: ["flag-player", row.player_id, row.generation], queryFn: () => getPlayerFlags(row.player_id), enabled: expanded });
  const review = useMutation({
    mutationFn: (generation: number) => markPlayerFlagsReviewed(row.player_id, generation),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["flag-player", row.player_id] }); onReviewed(); },
    onError: () => {
      // A new distinct finding may have arrived since this row loaded.
      void qc.invalidateQueries({ queryKey: ["flags"] });
      void qc.invalidateQueries({ queryKey: ["flag-player", row.player_id] });
    },
  });
  const reasons = parseReasons(row.latest_reasons);
  return <>
    <tr className="border-t border-border/50 hover:bg-accent/30">
      <td className="px-3 py-2 text-xs whitespace-nowrap"><Link to={`/players/${encodeURIComponent(row.player_id)}`} className="text-blue-300 hover:underline" title={row.player_id}>{row.player_id.slice(0, 10)}…</Link></td>
      <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">{formatTimestamp(row.first_ts)}</td>
      <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">{formatTimestamp(row.last_ts)}</td>
      <td className="px-3 py-2 text-xs tabular-nums">{row.total_count}</td>
      <td className="px-3 py-2 text-xs max-w-xs"><Badge variant="secondary" className="text-[10px] mr-1">{row.latest_source ? SOURCE_LABEL[row.latest_source] ?? row.latest_source : "Unknown"}</Badge><span className="text-muted-foreground" title={reasons.join("; ")}>{reasons[0] ?? "—"}{reasons.length > 1 && ` (+${reasons.length - 1} more)`}</span></td>
      <td className="px-3 py-2"><div className="flex flex-wrap gap-1"><Badge variant={row.reviewed ? "success" : "destructive"} className="text-[10px]">{row.reviewed ? "Reviewed" : "Unreviewed"}</Badge>{row.banned && <Badge variant="outline" className="text-[10px]">Banned</Badge>}{row.whitelisted && <Badge variant="outline" className="text-[10px]">Dev account</Badge>}</div></td>
      <td className="px-3 py-2 text-right whitespace-nowrap">
        <Button size="sm" variant="ghost" onClick={onToggle} aria-expanded={expanded} className="h-7 px-2 text-xs">{expanded ? "Hide" : "Details"}</Button>
        <Button size="sm" variant="secondary" className="h-7 px-2 text-xs ml-1" onClick={() => onRescan(detail.data?.summary.generation ?? row.generation)} disabled={review.isPending || banPending}>{rescanning ? "Rescanning…" : "Rescan"}</Button>
        {!row.reviewed && <Button size="sm" variant="secondary" className="h-7 px-2 text-xs ml-1" onClick={() => review.mutate(detail.data?.summary.generation ?? row.generation)} disabled={review.isPending || banPending}>{review.isPending ? "Marking…" : "Mark reviewed"}</Button>}
        {!row.banned && <Button size="sm" variant="destructive" className="h-7 px-2 text-xs ml-1" title="Permanently ban and mark reviewed" onClick={() => onQuickBan(detail.data?.summary.generation ?? row.generation)} disabled={review.isPending || banPending}>{banning ? "Banning…" : "Quick Ban"}</Button>}
      </td>
    </tr>
    {(expanded || review.error) && <tr className="border-t border-border/30 bg-muted/20"><td colSpan={7} className="px-3 py-3"><div className="space-y-3 text-xs">
      {review.error && <Alert variant="destructive"><AlertDescription>{errorMessage(review.error)}. Flags were refreshed; check the latest findings before retrying.</AlertDescription></Alert>}
      {expanded && <p className="font-mono text-muted-foreground break-all">Player ID: {row.player_id}</p>}
      {expanded && (detail.isPending ? <p className="text-muted-foreground">Loading retained evidence…</p> : detail.error ? <Alert variant="destructive"><AlertDescription>{errorMessage(detail.error)}</AlertDescription></Alert> : detail.data ? <FlagDetails rows={detail.data.details} /> : null)}
    </div></td></tr>}
  </>;
}
